import { memo, useMemo } from "react";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import {
  NO_FISCAL_DEVICE_VALUE,
  type DevicePaymentMethod,
  type DevicePaymentTypeSlot,
} from "../../../../../api/devices";
import type { PaymentMethod } from "../../../../../api/paymentMethods";
import { CardIcon } from "../modal/deviceModalIcons";
import {
  DeviceSectionCard,
  DeviceSectionHeader,
  EYEBROW_CLASS,
} from "../modal/deviceModalParts";

interface Props {
  paymentMethods: PaymentMethod[];
  value: DevicePaymentMethod[];
  onChange: (next: DevicePaymentMethod[]) => void;
  // Слотове от фискалния драйвер (ако принтерът ги е обявил). Стойността се
  // избира ВИНАГИ от менюто: конфигурираните слотове + „БЕЗ ФИСКАЛИЗИРАНЕ";
  // без слотове менюто съдържа само „БЕЗ ФИСКАЛИЗИРАНЕ". Ръчно писане няма.
  slots: DevicePaymentTypeSlot[] | null;
  isNarrow: boolean;
}

// Празна стойност = методът не се ползва на устройството (не се праща).
const NOT_USED = "";

/**
 * Секция „Методи на плащане" на фискалното устройство — за всеки метод слот
 * от принтера, „БЕЗ ФИСКАЛИЗИРАНЕ" или „Не се ползва". Праща само попълнените
 * редове, в реда на методите — както досегашното поле.
 */
const FiscalPaymentMethodsSection = ({ paymentMethods, value, onChange, slots, isNarrow }: Props) => {
  const { t, i18n } = useTranslation();
  const slotOptions = useMemo(() => {
    const fromPrinter = Array.isArray(slots)
      ? slots.filter((slot) => slot.configured).map((slot) => ({ value: slot.slot, label: slot.name }))
      : [];
    return [
      ...fromPrinter,
      { value: NO_FISCAL_DEVICE_VALUE, label: t("devicePaymentMethods.noFiscalOption") },
    ];
  }, [slots, t]);
  const options = [{ value: NOT_USED, label: t("devices.fiscalModal.notUsed") }, ...slotOptions];
  const language = i18n.language?.startsWith("en") ? "en" : "bg";

  const valueByMethodId = new Map<number, string>();
  for (const methodValue of value) valueByMethodId.set(methodValue.paymentMethodId, methodValue.value);

  const handleChange = (paymentMethodId: number, nextValue: string) => {
    const nextList: DevicePaymentMethod[] = [];
    for (const paymentMethod of paymentMethods) {
      const methodValue = (
        paymentMethod.id === paymentMethodId ? nextValue : (valueByMethodId.get(paymentMethod.id) ?? "")
      ).trim();
      if (methodValue.length > 0) {
        nextList.push({ paymentMethodId: paymentMethod.id, value: methodValue });
      }
    }
    onChange(nextList);
  };

  const isSlot = (methodValue: string) =>
    methodValue !== NOT_USED && methodValue !== NO_FISCAL_DEVICE_VALUE;
  const fiscalizedCount = paymentMethods.filter((paymentMethod) =>
    isSlot(valueByMethodId.get(paymentMethod.id) ?? NOT_USED),
  ).length;
  const slotLabelOf = (methodValue: string) =>
    slotOptions.find((option) => option.value === methodValue)?.label ?? methodValue;

  return (
    <DeviceSectionCard>
      <DeviceSectionHeader
        icon={<CardIcon size={18} />}
        iconClassName="bg-ok-tint text-ok-deep"
        title={t("devices.fiscalModal.methodsTitle")}
        description={t("devices.fiscalModal.methodsDesc")}
        shortDescription={t("devices.fiscalModal.methodsDescShort")}
        isNarrow={isNarrow}
      />

      <div className={`fiscal-pm flex flex-col ${isNarrow ? "gap-1.5 p-4" : "gap-0.5 px-4 py-3"}`}>
        {paymentMethods.length === 0 ? (
          <div className="rounded-[10px] border border-dashed border-shade-2 bg-canvas p-3.5 text-center text-[12.5px] text-muted">
            {t("devicePaymentMethods.noPaymentMethods")}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 pb-1">
              <span className={EYEBROW_CLASS}>{t("devices.fiscalModal.methodLabel")}</span>
              <span className="rounded-full bg-ok-tint px-[9px] py-[3px] text-[11px] font-bold text-ok-deep">
                {t("devices.fiscalModal.fiscalizedPill", { count: fiscalizedCount })}
              </span>
              {!isNarrow && (
                <span className={`ml-auto ${EYEBROW_CLASS}`}>{t("devices.fiscalModal.slotLabel")}</span>
              )}
            </div>
            {paymentMethods.map((paymentMethod) => {
              const methodValue = valueByMethodId.get(paymentMethod.id) ?? NOT_USED;
              const hasSlot = isSlot(methodValue);
              const isNoFiscal = methodValue === NO_FISCAL_DEVICE_VALUE;
              return (
                <div
                  key={paymentMethod.id}
                  className={`flex flex-wrap items-center gap-x-2.5 gap-y-2 border-t border-shade-1 ${
                    isNarrow ? "py-2" : "py-1.5"
                  }`}
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      hasSlot ? "bg-ok" : isNoFiscal ? "bg-brand" : "bg-fill-3"
                    }`}
                  />
                  <div className="flex min-w-0 flex-[1_1_110px] flex-col">
                    <span className="truncate text-[13px] font-semibold leading-[1.3] text-ink">
                      {language === "en" ? paymentMethod.name_en : paymentMethod.name_bg}
                    </span>
                    <span
                      className={`truncate text-[11px] leading-[1.35] ${
                        hasSlot ? "text-ok-deep" : isNoFiscal ? "text-warn-ink" : "text-muted"
                      }`}
                    >
                      {hasSlot
                        ? t("devices.fiscalModal.hintSlot", { slot: slotLabelOf(methodValue) })
                        : isNoFiscal
                          ? t("devices.fiscalModal.hintNoFiscal")
                          : t("devices.fiscalModal.hintNotUsed")}
                    </span>
                  </div>
                  <Select
                    value={methodValue}
                    onChange={(nextValue) => handleChange(paymentMethod.id, nextValue ?? NOT_USED)}
                    options={options}
                    // Слот → зелено поле (scoped CSS в FiscalPrinterModal — вътрешен antd клас).
                    className={`${hasSlot ? "fiscal-pm-slot" : ""} ${
                      isNarrow ? "!w-full" : "!w-[198px] max-w-full"
                    }`}
                  />
                </div>
              );
            })}
          </>
        )}
      </div>
    </DeviceSectionCard>
  );
};

// memo: при писане на името в хедъра секцията не се прерисува.
export default memo(FiscalPaymentMethodsSection);
